// Centralised constants for the cloud-auth-ui SPA. Per the engineering
// standards no string/number magic values may live in components.

// The login surface is served from the main app host (e.g.
// app.talktofred.ai — FRED_CLOUD_LOGIN_HOST on the cloud), where the
// bare root IS the sign-in surface for signed-out visitors. ROUTE_LOGIN
// stays as the canonical explicit path (/login) and backward-compatible
// alias; the retired dedicated login. subdomain 301s here.
export const ROUTE_ROOT = '/';
export const ROUTE_LOGIN = '/login';
export const ROUTE_MAGIC = '/magic';
export const ROUTE_OIDC_RETURN = '/oidc/return';
export const ROUTE_EXPIRED = '/expired';

export const QUERY_PARAM_NEXT = 'next';
export const QUERY_PARAM_CODE = 'code';
export const QUERY_PARAM_REASON = 'reason';
export const QUERY_PARAM_TOKEN = 'token';

// Where to send a visitor who arrived with no explicit, safe `?next=`.
// Empty by design: the SPA cannot know the principal's surface before
// sign-in, so it must NOT presume `/admin`. Leaving `next` empty lets the
// cloud's OIDC callback land each principal on its surface-appropriate
// home (`/admin` for admins, `/user` for user-surface principals). A
// non-empty default here would force every visitor — admin or not —
// toward `/admin`, where a non-admin renders a dead, 403-ing shell and is
// bounced straight back to /login in a loop.
export const DEFAULT_NEXT_PATH = '';

export const API_BASE_PATH = '/v1/auth';
export const API_PATH_PASSWORD = '/password';
export const API_PATH_MAGIC = '/magic-link';

// OIDC sign-in is a server-driven BFF flow, not an XHR endpoint: the
// button is a plain top-level navigation to this path, which 302s the
// browser to the external IdP. The PKCE verifier never touches the SPA.
export const OIDC_START_PATH = '/v1/auth/oidc/start';
export const OIDC_CALLBACK_PATH = '/v1/auth/oidc/callback';

export const HEADER_CONTENT_TYPE = 'Content-Type';
export const CONTENT_TYPE_JSON = 'application/json';

export const HTTP_OK_MIN = 200;
export const HTTP_OK_MAX = 299;

export const MAGIC_RESEND_COOLDOWN_MS = 30_000;

export const PRODUCT_NAME = 'Talk to Fred';
export const TAGLINE = 'Sign in to manage your Fred Cloud workspace.';
// Default copyright holder. The server can override it at request time
// via the `window.__FRED_BRANDING__` global (env: FRED_CLOUD_COPYRIGHT_HOLDER)
// without rebuilding the SPA; the reader `??`s the injected value over
// this constant.
export const COPYRIGHT_HOLDER = 'talktofred.ai';

export const BRANDING_GLOBAL = '__FRED_BRANDING__' as const;

export interface Branding {
  copyright_holder: string;
}

export const DEFAULT_BRANDING: Branding = {
  copyright_holder: COPYRIGHT_HOLDER,
};

// `window.__FRED_AUTH_CAPS__` is injected into the SPA shell by the
// cloud's GET /login route at request time. Each flag toggles a
// control on the login screen: when false, the matching server
// endpoint is not wired on this deployment and the SPA hides the
// control instead of letting users click into a 404. When the global
// is missing (dev server, hot-reload, deep-linked SPA) every flag
// degrades to false so the screen shows a safe "not configured" notice
// rather than auto-bouncing into an unwired endpoint.
export interface AuthCapabilities {
  password: boolean;
  magic_link: boolean;
  oidc: boolean;
}

export const DEFAULT_AUTH_CAPABILITIES: AuthCapabilities = {
  password: false,
  magic_link: false,
  oidc: false,
};

export const AUTH_CAPS_GLOBAL = '__FRED_AUTH_CAPS__' as const;

// Reason emitted by the cloud when a CSRF-guarded auth-flow POST failed
// the token check (and by api.ts when a POST still 419s after the
// one-shot cookie refresh). Keep in sync with
// App\Auth\AuthFailureRef::REASON_CSRF_EXPIRED on the cloud.
export const REASON_CSRF_EXPIRED = 'csrf_expired';

// Mapping of server-side `reason` codes to human copy for the sign-in
// surfaces (logout, expired session, and the OIDC relying-party flow).
export const REASON_LABEL: Record<string, string> = {
  expired: 'This sign-in link has expired. Request a new one below.',
  invalid: 'We could not verify that link. Request a new one below.',
  invalid_signature: 'This sign-in link could not be verified. Request a new one below.',
  malformed: 'This sign-in link is malformed. Request a new one below.',
  missing_field: 'This sign-in link is missing required information. Request a new one below.',
  logged_out: 'You have been signed out.',
  missing_token: 'You need to sign in to continue.',
  rate_limited: 'Too many attempts. Wait a moment and try again.',
  // Bounces from the authenticated shells (api-adapter.ts) and the
  // /admin surface gate (api routes/web.php) when a live session is
  // rejected by a surface it may not load. Keep these codes in sync with
  // those emitters so the user always learns why they landed back here.
  session_expired: 'Your session expired. Please sign in again.',
  forbidden: 'Your account doesn’t have access to that area.',
  insufficient_role: 'That area is limited to administrators.',
  // Pre-render bounces from the /user workspace gate (EnsureUserWorkspace).
  no_workspace: 'Your account doesn’t have an active workspace yet. Check your subscription or contact your administrator.',
  workspace_unavailable: 'We couldn’t open your workspace just now. Sign in again in a moment.',
  workspace_access_revoked: 'Your workspace administrator access was removed or the invitation expired. Ask the workspace owner to invite you again.',
  replayed: 'This sign-in link has already been used. Request a new one below.',
  revoked: 'This sign-in link was revoked. Request a new one below.',
  [REASON_CSRF_EXPIRED]:
    'That didn’t go through — your secure session refreshed. Please try again.',
  // OIDC relying-party flow. Keep in sync with
  // App\Auth\Oidc\OidcClientException::REASON_* on the cloud. The
  // restartable subset (RESTARTABLE_REASONS) is normally recovered
  // transparently by a one-shot auto-restart; this copy shows when the
  // restart guard has already spent its attempt.
  oidc_unconfigured: 'Single sign-on is not configured for this deployment. Contact your administrator.',
  oidc_discovery_failed: 'We couldn’t reach the sign-in provider. Start again in a moment.',
  oidc_state_mismatch: 'That sign-in attempt expired or was superseded by another window.',
  oidc_exchange_failed: 'That sign-in code was already used or has expired.',
  oidc_provider_error: 'Your sign-in provider reported an error or the sign-in was cancelled.',
  oidc_verification_failed: 'We couldn’t verify the identity returned by your provider.',
};

// Failure reasons the SPA may recover from TRANSPARENTLY by restarting
// the OIDC flow once (fresh state, preserved `next`): stale/superseded
// state from a parallel window, a consumed single-use code, a transient
// discovery/verification miss. `oidc_provider_error` is deliberately
// excluded — it can mean the user cancelled at the IdP, and bouncing
// them straight back would fight their decision. `oidc_unconfigured` is
// deterministic misconfiguration; restarting cannot fix it.
export const RESTARTABLE_REASONS: ReadonlySet<string> = new Set([
  'oidc_state_mismatch',
  'oidc_exchange_failed',
  'oidc_verification_failed',
  'oidc_discovery_failed',
]);

// Support-searchable diagnostic refs rendered subtly on error states
// and logged server-side with structured context — the auth-flow
// counterpart of the RAG outage canary ("reference code FRED-7"). Keep
// in sync with App\Auth\AuthFailureRef on the cloud; never renumber an
// existing ref. Reasons without a ref (e.g. `logged_out`) are
// informational, not failures.
export const REASON_REF: Record<string, string> = {
  oidc_unconfigured: 'FRED-40',
  oidc_discovery_failed: 'FRED-41',
  oidc_state_mismatch: 'FRED-42',
  oidc_exchange_failed: 'FRED-43',
  oidc_provider_error: 'FRED-44',
  oidc_verification_failed: 'FRED-45',
  [REASON_CSRF_EXPIRED]: 'FRED-46',
  session_expired: 'FRED-47',
  forbidden: 'FRED-48',
  insufficient_role: 'FRED-49',
  no_workspace: 'FRED-50',
  workspace_unavailable: 'FRED-51',
  missing_token: 'FRED-52',
  rate_limited: 'FRED-53',
  workspace_access_revoked: 'FRED-54',
};
